import { Mail, Reply } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { HeroVorgangMessageBody } from "./hero-vorgang-message-body";
import {
  HERO_VORGANG_BUBBLE_INBOUND_BG,
  HERO_VORGANG_BUBBLE_OUTBOUND_BG,
  HERO_VORGANG_CHANNEL_HEADER_BG,
  HERO_VORGANG_CHANNEL_HEADER_BORDER,
} from "./hero-vorgang-tokens";

export type HeroVorgangMessageData = {
  readonly id: string;
  readonly direction: "inbound" | "outbound";
  readonly channel: "email" | "portal";
  readonly subject?: string;
  readonly fromLine?: string;
  readonly authorName: string;
  readonly authorInitials: string;
  readonly authorAvatarBg: string;
  readonly authorAvatarFg: string;
  readonly roleLabel?: string;
  readonly timestamp: string;
  readonly fullPlain: string;
  readonly signatureName?: string;
};

function bubbleShellStyle(direction: "inbound" | "outbound"): CSSProperties {
  const isOutbound: boolean = direction === "outbound";
  return {
    width: "100%",
    maxWidth: isOutbound ? "72%" : "78%",
    alignSelf: isOutbound ? "flex-end" : "flex-start",
    borderRadius: 12,
    background: isOutbound
      ? HERO_VORGANG_BUBBLE_OUTBOUND_BG
      : HERO_VORGANG_BUBBLE_INBOUND_BG,
    boxShadow: "0 1px 2px rgba(11,15,25,0.04)",
    overflow: "hidden",
  };
}

function EmailChannelHeader(props: {
  readonly subject: string;
  readonly fromLine: string;
  readonly isOutbound: boolean;
}): ReactNode {
  const t = useTranslations("marketing.heroVorgangMessage");
  return (
    <div
      style={{
        margin: "12px 12px 0",
        padding: "8px 12px",
        borderRadius: 6,
        border: `1px solid ${HERO_VORGANG_CHANNEL_HEADER_BORDER}`,
        background: HERO_VORGANG_CHANNEL_HEADER_BG,
        fontSize: 11,
        lineHeight: 1.45,
        color: "rgba(11,15,25,0.68)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <span
          style={{
            color: "rgba(11,15,25,0.52)",
            display: "inline-flex",
            marginTop: 1,
          }}
        >
          {props.isOutbound ? (
            <Reply width={14} height={14} strokeWidth={1.75} />
          ) : (
            <Mail width={14} height={14} strokeWidth={1.75} />
          )}
        </span>
        <span style={{ fontWeight: 600, color: "rgba(11,15,25,0.80)" }}>
          {props.subject}
        </span>
      </div>
      <div style={{ paddingLeft: 20, marginTop: 2 }}>
        <span style={{ color: "rgba(11,15,25,0.45)" }}>{t("von")} </span>
        <span style={{ color: "rgba(11,15,25,0.72)" }}>{props.fromLine}</span>
      </div>
    </div>
  );
}

export function HeroVorgangMessage(props: {
  readonly message: HeroVorgangMessageData;
}): ReactNode {
  const msg: HeroVorgangMessageData = props.message;
  const isOutbound: boolean = msg.direction === "outbound";
  const showEmailHeader: boolean =
    msg.channel === "email" &&
    msg.subject !== undefined &&
    msg.fromLine !== undefined;

  return (
    <div style={bubbleShellStyle(msg.direction)}>
      {showEmailHeader ? (
        <EmailChannelHeader
          subject={msg.subject ?? ""}
          fromLine={msg.fromLine ?? ""}
          isOutbound={isOutbound}
        />
      ) : null}

      <div style={{ padding: "12px 16px 14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: msg.authorAvatarBg,
              color: msg.authorAvatarFg,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {msg.authorInitials}
          </span>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0B0F19" }}>
              {msg.authorName}
            </span>
            {msg.roleLabel !== undefined ? (
              <span style={{ fontSize: 12, color: "rgba(11,15,25,0.45)" }}>
                {msg.roleLabel}
              </span>
            ) : null}
            <span
              style={{
                marginLeft: "auto",
                fontSize: 12,
                color: "rgba(11,15,25,0.40)",
                whiteSpace: "nowrap",
              }}
            >
              {msg.timestamp}
            </span>
          </div>
        </div>

        <HeroVorgangMessageBody
          channel={msg.channel}
          direction={msg.direction}
          fullPlain={msg.fullPlain}
          signatureName={msg.signatureName}
        />
      </div>
    </div>
  );
}
